// decimate.js — viewport line-series downsampling for fast Plot rendering.
//
// A plot is only ~W pixels wide, so a series of N≫W points wastes work:
// thousands of samples collapse into one pixel column. We downsample the
// VISIBLE x-range to O(W) points before building the SVG path — the render
// cost drops from O(N) to O(W) while staying visually faithful.
//
// Algorithms (universal Plot, not audio-specific):
//   'm4'   — per pixel-column keep {first, min, max, last}. Pixel-faithful:
//            smooth data → a thin line; spikes and the true oscillation
//            extent are preserved (what MATLAB does for big lines). Default.
//   'lttb' — Largest-Triangle-Three-Buckets: smoother for trends, ~1 pt per
//            column, but can hide narrow spikes. Opt-in.
//   'none' — no decimation (raw slice of the visible range).
//
// x is assumed ascending (plot(y) → 1..N; plot(x,y) with sorted x); buckets
// are by x-position so they line up with pixel columns. A non-finite y
// (NaN/Inf) is a gap; it is preserved when it lands on a bucket endpoint.

// First index i with x[i] >= target (x ascending). Returns x.length if none.
function lowerBound(x, target) {
  let lo = 0, hi = x.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (x[mid] < target) lo = mid + 1; else hi = mid;
  }
  return lo;
}

// Index range [i0, i1) covering the visible x-window, padded by one sample
// each side so the line enters/leaves the viewport instead of stopping at
// the edge. Clamped to [0, n].
export function visibleRange(x, x0, x1) {
  const n = x.length;
  if (n === 0) return [0, 0];
  let i0 = lowerBound(x, x0);
  let i1 = lowerBound(x, x1);
  if (i0 > 0) i0 -= 1;            // one point left of the window
  if (i1 < n) i1 += 1;           // one point right of the window
  if (i1 > n) i1 = n;
  if (i0 < 0) i0 = 0;
  if (i1 < i0) i1 = i0;
  return [i0, i1];
}

// True when the finite x values never decrease (NaN/Inf gaps are ignored).
// Both visibleRange's binary search and the M4 pixel-column bucketing assume
// ascending x; a parametric / multi-segment line — a voronoi diagram, a
// triplot mesh, any null-separated set of segments that double back in x —
// violates that. Such a series must render RAW: windowing it by x would slice
// a bogus index range and drop most of the segments.
export function isMonotonicX(x) {
  let prev = -Infinity;
  for (let i = 0; i < x.length; i++) {
    const v = x[i];
    if (!Number.isFinite(v)) continue;     // gap marker — skip
    if (v < prev) return false;
    prev = v;
  }
  return true;
}

// M4: {first, min, max, last} per pixel column over the visible range.
// Returns { x:[], y:[] } with at most 4*width points, in x order.
export function decimateM4(x, y, x0, x1, width) {
  const [i0, i1] = visibleRange(x, x0, x1);
  const outX = [], outY = [];
  if (i1 <= i0) return { x: outX, y: outY };
  const cols = Math.max(1, width | 0);
  const span = (x1 - x0) || 1;
  const bucketOf = (xv) => {
    let b = Math.floor(((xv - x0) / span) * cols);
    if (b < 0) b = 0; else if (b >= cols) b = cols - 1;
    return b;
  };

  let curBucket = -1;
  let bFirst = i0, bLast = i0, bMinI = i0, bMaxI = i0, bMinY = Infinity, bMaxY = -Infinity;
  const flush = () => {
    if (curBucket < 0) return;
    // first / min / max / last in index (x) order, de-duplicated.
    const idxs = [bFirst, bMinI, bMaxI, bLast].sort((a, b) => a - b);
    let prev = -1;
    for (const ii of idxs) {
      if (ii !== prev) { outX.push(x[ii]); outY.push(y[ii]); prev = ii; }
    }
  };

  for (let i = i0; i < i1; i++) {
    const xv = x[i], yv = y[i];
    const b = bucketOf(xv);
    if (b !== curBucket) {
      flush();
      curBucket = b;
      bFirst = i; bLast = i; bMinI = i; bMaxI = i;
      bMinY = Number.isFinite(yv) ? yv : Infinity;
      bMaxY = Number.isFinite(yv) ? yv : -Infinity;
    } else {
      bLast = i;
      if (Number.isFinite(yv)) {
        if (yv < bMinY) { bMinY = yv; bMinI = i; }
        if (yv > bMaxY) { bMaxY = yv; bMaxI = i; }
      }
    }
  }
  flush();
  return { x: outX, y: outY };
}

// M2: {min, max} per pixel column (2 points, vs M4's 4) — a pixel can only
// show a column's vertical extent [min..max] anyway, so first/last are
// invisible. Halves M4's point count while keeping spikes / true extent;
// a lighter mode for smoother panning of dense data.
export function decimateM2(x, y, x0, x1, width) {
  const [i0, i1] = visibleRange(x, x0, x1);
  const outX = [], outY = [];
  if (i1 <= i0) return { x: outX, y: outY };
  const cols = Math.max(1, width | 0);
  const span = (x1 - x0) || 1;
  const bucketOf = (xv) => {
    let b = Math.floor(((xv - x0) / span) * cols);
    if (b < 0) b = 0; else if (b >= cols) b = cols - 1;
    return b;
  };

  let curBucket = -1, bMinI = i0, bMaxI = i0, bMinY = Infinity, bMaxY = -Infinity;
  const flush = () => {
    if (curBucket < 0) return;
    const a = Math.min(bMinI, bMaxI), b = Math.max(bMinI, bMaxI);   // x order
    outX.push(x[a]); outY.push(y[a]);
    if (b !== a) { outX.push(x[b]); outY.push(y[b]); }
  };

  for (let i = i0; i < i1; i++) {
    const xv = x[i], yv = y[i];
    const bk = bucketOf(xv);
    if (bk !== curBucket) {
      flush();
      curBucket = bk; bMinI = i; bMaxI = i;
      bMinY = Number.isFinite(yv) ? yv : Infinity;
      bMaxY = Number.isFinite(yv) ? yv : -Infinity;
    } else if (Number.isFinite(yv)) {
      if (yv < bMinY) { bMinY = yv; bMinI = i; }
      if (yv > bMaxY) { bMaxY = yv; bMaxI = i; }
    }
  }
  flush();
  return { x: outX, y: outY };
}

// LTTB: ~`threshold` points preserving visual shape (smooth trends).
export function decimateLTTB(x, y, x0, x1, threshold) {
  const [i0, i1] = visibleRange(x, x0, x1);
  const n = i1 - i0;
  const outX = [], outY = [];
  if (n <= 0) return { x: outX, y: outY };
  if (threshold >= n || threshold < 3) {
    for (let i = i0; i < i1; i++) { outX.push(x[i]); outY.push(y[i]); }
    return { x: outX, y: outY };
  }
  const bucketSize = (n - 2) / (threshold - 2);
  let a = i0;                              // first point is always kept
  outX.push(x[i0]); outY.push(y[i0]);
  for (let i = 0; i < threshold - 2; i++) {
    // Average of the NEXT bucket (the third "triangle" vertex).
    let avgStart = i0 + Math.floor((i + 1) * bucketSize) + 1;
    let avgEnd = i0 + Math.floor((i + 2) * bucketSize) + 1;
    if (avgEnd > i1) avgEnd = i1;
    let avgX = 0, avgY = 0;
    const avgN = Math.max(1, avgEnd - avgStart);
    for (let j = avgStart; j < avgEnd; j++) { avgX += x[j]; avgY += y[j]; }
    avgX /= avgN; avgY /= avgN;
    // Pick the point in THIS bucket with the largest triangle area.
    const rangeStart = i0 + Math.floor(i * bucketSize) + 1;
    const rangeEnd = i0 + Math.floor((i + 1) * bucketSize) + 1;
    const ax = x[a], ay = y[a];
    let maxArea = -1, maxIdx = rangeStart;
    for (let j = rangeStart; j < rangeEnd && j < i1; j++) {
      const area = Math.abs((ax - avgX) * (y[j] - ay) - (ax - x[j]) * (avgY - ay));
      if (area > maxArea) { maxArea = area; maxIdx = j; }
    }
    outX.push(x[maxIdx]); outY.push(y[maxIdx]);
    a = maxIdx;
  }
  outX.push(x[i1 - 1]); outY.push(y[i1 - 1]);
  return { x: outX, y: outY };
}

// ── LOD pyramid — keep per-frame decimation O(W) at ANY zoom ────────────
//
// Decimating the raw series every frame is O(visible): cheap zoomed-in,
// but O(N) zoomed-out (the whole series falls in view). An extrema-
// preserving pyramid fixes that: level 0 is the raw series, each next level
// is ≤ half the points (M4, so spikes survive). At render we decimate from
// the coarsest level that still has enough points in view → O(W) always.

// Build the pyramid. O(N) total (geometric). Stops at ~baseTarget points.
export function buildPyramid(x, y, baseTarget = 8000) {
  const levels = [{ x, y }];                 // level 0 = raw
  // M4 (used to coarsen each level) assumes ascending x — a non-monotonic
  // series can't be pyramided. Keep just the raw level; decimateLOD renders
  // it unchanged.
  if (!isMonotonicX(x)) return levels;
  let cx = x, cy = y;
  while (cx.length > baseTarget) {
    const cols = Math.max(1, Math.ceil(cx.length / 8));   // M4 → ≤ half
    const d = decimateM4(cx, cy, cx[0], cx[cx.length - 1], cols);
    if (d.x.length >= cx.length) break;       // safety: no progress
    levels.push(d);
    cx = d.x; cy = d.y;
  }
  return levels;
}

// Decimate from the coarsest level with ≥ 2·width points in [x0,x1] (finer
// as you zoom in). Per-frame cost is O(width) regardless of series size.
export function decimateLOD(levels, x0, x1, width, algo = 'm4') {
  if (!levels || !levels.length) return { x: [], y: [], decimated: false, n: 0 };
  // Non-monotonic x (parametric / multi-segment): the pyramid + x-window are
  // both invalid, so render the raw series unchanged.
  if (!isMonotonicX(levels[0].x)) {
    const rx = levels[0].x, ry = levels[0].y;
    return { x: rx, y: ry, decimated: false, n: rx.length };
  }
  const w = Math.max(1, width | 0);
  let chosen = levels[0];                     // finest (raw) fallback
  for (let i = levels.length - 1; i >= 0; i--) {
    const [a, b] = visibleRange(levels[i].x, x0, x1);
    if (b - a >= 2 * w || i === 0) { chosen = levels[i]; break; }
  }
  return decimateSeries(chosen.x, chosen.y, x0, x1, w, algo);
}

// Dispatcher. Returns the raw visible slice when there's nothing to gain
// (visible count already ≤ 2·width) or algo === 'none'. `decimated` tells
// the caller whether downsampling actually happened. width is the plot's
// pixel width.
export function decimateSeries(x, y, x0, x1, width, algo = 'm4') {
  const w = Math.max(1, width | 0);
  // Non-monotonic x can't be x-windowed (the binary search would slice a bogus
  // range and drop segments) — hand back the whole series untouched.
  if (!isMonotonicX(x)) return { x, y, decimated: false, n: x.length };
  const [i0, i1] = visibleRange(x, x0, x1);
  const n = i1 - i0;
  if (algo === 'none' || n <= w * 2) {
    const ox = [], oy = [];
    for (let i = i0; i < i1; i++) { ox.push(x[i]); oy.push(y[i]); }
    return { x: ox, y: oy, decimated: false, n };
  }
  const out = algo === 'lttb' ? decimateLTTB(x, y, x0, x1, w)
            : algo === 'm2'   ? decimateM2(x, y, x0, x1, w)
            :                   decimateM4(x, y, x0, x1, w);
  return { x: out.x, y: out.y, decimated: true, n };
}
